// Libs
import classNames from 'classnames/bind';
import moment from 'moment';
import { Avatar, Box, IconButton, Portal, Stack, TextField, Typography } from '@mui/material';
import DatePicker, { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import { forwardRef, InputHTMLAttributes, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
import { BaseLabel } from '~/components/common';
// Others
import { DateFormatEnum } from '~/utils/enum/common';
import { TModeDatePickerType } from '~/utils/type/common';
import { EMPTY_STRING } from '~/utils/constants/common';
// Styles, images, icons
import styles from './BaseDatePicker.module.scss';
import { icons } from '~/assets';
import 'react-datepicker/dist/react-datepicker.css';

type Props = {
  mode?: TModeDatePickerType;
  label?: string;
  value?: Date | string | { startDate: string; endDate: string };
  required?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  height?: number | string;
  width?: number | string;
  maxDate?: Date;
  minDate?: Date;
  placeholder?: string;
  onChange?: (date: string | { startDate: string; endDate: string }) => void;
};

const cx = classNames.bind(styles);

type TextFieldInputProps = InputHTMLAttributes<HTMLInputElement>;

const BaseDatePicker = (props: Props) => {
  //#region Destructuring Props
  const {
    mode = 'date',
    label,
    value,
    errorMessage,
    disabled = false,
    required = false,
    height = 36,
    width,
    placeholder,
    minDate,
    maxDate,
    onChange,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [rangeDate, setRangeDate] = useState<[Date | null, Date | null]>([null, null]);
  const [dateText, setDateText] = useState<string>(EMPTY_STRING);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (mode === 'rangeDate') {
      handleValueRangeDate();
      return;
    }

    if (!value || typeof value === 'object') {
      setSelectedDate(null);
      setDateText(EMPTY_STRING);
      return;
    }

    if (mode === 'date') {
      handleValueDate(value);
      return;
    }

    if (mode === 'time') {
      handleValueTime();
      return;
    }
  }, [value]);

  //#endregion Implement Hook

  //#region Handle Function
  const handleValueDate = (value: string) => {
    const parsed = moment(value);
    if (!parsed.isValid()) {
      setSelectedDate(null);
      setDateText(EMPTY_STRING);
      return;
    }
    const date = parsed.toDate();
    const dateString = parsed.format(DateFormatEnum.M_DD_YYYY);
    setSelectedDate(date);
    setDateText(dateString);
  };

  const handleValueTime = () => {
    const today = moment().format(DateFormatEnum.YYYY_MM_DD);
    const dateTime = moment(`${today} ${value}`, `${DateFormatEnum.YYYY_MM_DD} HH:mm`);
    if (!dateTime.isValid()) {
      setSelectedDate(null);
      setDateText(EMPTY_STRING);
      return;
    }
    const date = dateTime.toDate();
    const dateString = dateTime.format('hh:mm A');
    setSelectedDate(date);
    setDateText(dateString);
  };

  const handleValueRangeDate = () => {
    const { startDate, endDate } = (value ?? {}) as { startDate?: string; endDate?: string };
    const start = startDate ? moment(startDate).toDate() : null;
    const end = endDate ? moment(endDate).toDate() : null;
    setRangeDate([start, end]);

    if (start && end) {
      setDateText(
        `${moment(start).format(DateFormatEnum.M_DD_YYYY)} - ${moment(end).format(DateFormatEnum.M_DD_YYYY)}`
      );
    } else if (start) {
      setDateText(moment(start).format(DateFormatEnum.M_DD_YYYY));
    } else if (end) {
      setDateText(moment(end).format(DateFormatEnum.M_DD_YYYY));
    } else {
      setDateText(EMPTY_STRING);
    }
  };

  const handleChange = (date: Date | null) => {
    if (!date) return;

    if (isSameDate(selectedDate, date)) {
      clearValue();
      return;
    }

    setSelectedDate(date);
    if (mode === 'time') {
      const UIValue = moment(date).format('hh:mm A');
      const outputValue = moment(date).format('HH:mm');

      setDateText(UIValue);
      onChange?.(outputValue);
      return;
    }

    const formatted = moment(date).format(DateFormatEnum.YYYY_MM_DD);
    setDateText(moment(date).format(DateFormatEnum.M_DD_YYYY));
    onChange?.(formatted);
  };

  const isSameDate = (a: Date | null, b: Date | null) => {
    if (!a || !b) return false;

    if (mode === 'time') {
      return moment(a).format('HH:mm') === moment(b).format('HH:mm');
    }

    return moment(a).isSame(b, 'day');
  };

  const clearValue = () => {
    setSelectedDate(null);
    setRangeDate([null, null]);
    setDateText(EMPTY_STRING);

    if (mode === 'rangeDate') {
      onChange?.({ startDate: EMPTY_STRING, endDate: EMPTY_STRING });
    } else {
      onChange?.(EMPTY_STRING);
    }
  };

  const handleChangeRange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setRangeDate(dates);

    if (start && end) {
      const startDate = moment(start).format(DateFormatEnum.YYYY_MM_DD);
      const endDate = moment(end).format(DateFormatEnum.YYYY_MM_DD);
      setDateText(
        `${moment(start).format(DateFormatEnum.M_DD_YYYY)} - ${moment(end).format(DateFormatEnum.M_DD_YYYY)}`
      );
      onChange?.({ startDate, endDate });
    }
  };

  const TextFieldInput = forwardRef<HTMLInputElement, TextFieldInputProps>(({ onClick }, ref) => (
    <TextField
      fullWidth
      ref={ref}
      classes={{ root: cx('textField') }}
      sx={{}}
      slotProps={{
        input: {
          className: cx('textFieldInput', { disabled: disabled }),
          sx: {
            height,
            width,
          },
          readOnly: true,
          endAdornment: (
            <Avatar
              src={mode === 'time' ? icons.commonIconClock : icons.commonIconCalendar}
              className={cx('iconInput')}
            />
          ),
        },
        formHelperText: { className: cx('errorMessage') },
      }}
      disabled={disabled}
      onClick={onClick}
      value={dateText}
      placeholder={placeholder ? placeholder : t('common_text_placeholder_select')}
      helperText={errorMessage}
    />
  ));

  const renderCustomHeader = ({
    monthDate,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }: ReactDatePickerCustomHeaderProps) => (
    <Box className={cx('header')}>
      <IconButton onClick={decreaseMonth} disabled={prevMonthButtonDisabled} className={cx('iconButton')}>
        <img src={icons.commonIconPreviousPrimary} alt={t('common_alt_img')} />
      </IconButton>
      <Typography className={cx('titleHeader')}>{moment(monthDate).format(DateFormatEnum.MMMM_YYYY)}</Typography>
      <IconButton onClick={increaseMonth} disabled={nextMonthButtonDisabled} className={cx('iconButton')}>
        <img src={icons.commonIconNextPrimary} alt={t('common_alt_img')} />
      </IconButton>
    </Box>
  );
  //#endregion Handle Function

  return (
    <Stack component={'div'} id='baseDatePickerComponent' className={cx('container')} spacing={2}>
      {label && <BaseLabel label={label} required={required} />}

      <DatePicker
        {...(mode === 'rangeDate'
          ? {
              selectsRange: true,
              startDate: rangeDate[0] ?? undefined,
              endDate: rangeDate[1] ?? undefined,
              onChange: handleChangeRange,
            }
          : {
              selected: selectedDate,
              onChange: handleChange,
            })}
        portalId='baseDatePicker'
        popperContainer={Portal}
        popperPlacement='bottom-start'
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        showTimeSelectOnly={mode === 'time'}
        showTimeSelect={mode === 'time'}
        timeIntervals={mode === 'time' ? 30 : undefined}
        showTimeCaption={false}
        showPopperArrow={false}
        dateFormat={mode === 'time' ? DateFormatEnum.HH_mm : DateFormatEnum.YYYY_MM_DD}
        renderCustomHeader={(params) => renderCustomHeader({ ...params })}
        customInput={<TextFieldInput />}
      />
    </Stack>
  );
};

export default memo(BaseDatePicker);
